import { z } from 'zod'
import {
  DrugCollectionMethod,
  DrugPanelType,
  DrugScreeningVendor,
  DrugTestTrigger,
} from '../drug-screening'
import {
  OvertimePayMultiplier,
  OvertimePayTrigger,
} from './categories/billing-details'
import {
  TimesheetScheduleDay,
  TimesheetScheduleTime,
} from './categories/timekeeping'
import { CompanyFactSchemaVersion } from './core'
import {
  OnsiteStrategyV1FactValue,
  OnsiteStrategyV1FactValueSchema,
} from './onsite-strategy'

// ── Value-Structural Enums ──────────────────────────────────────────

export enum BreakTypeOption {
  PAID = 'PAID',
  AUTO_UNPAID = 'AUTO_UNPAID',
  MANUAL_UNPAID = 'MANUAL_UNPAID',
  NONE = 'NONE',
}

export enum PainPointBucket {
  LABOR_POOL_DEPTH = 'LABOR_POOL_DEPTH',
  WORKER_QUALITY = 'WORKER_QUALITY',
  WORKER_RETENTION = 'WORKER_RETENTION',
  OPERATIONAL_BURDEN = 'OPERATIONAL_BURDEN',
}

export enum PainPointIssue {
  LIMITED_LABOR_VOLUME = 'LIMITED_LABOR_VOLUME',
  LIMITED_HEADCOUNT_FLEXIBILITY = 'LIMITED_HEADCOUNT_FLEXIBILITY',
  LIMITED_GEOGRAPHIC_COVERAGE = 'LIMITED_GEOGRAPHIC_COVERAGE',
  SLOW_TIME_TO_FILL = 'SLOW_TIME_TO_FILL',
  POOR_WORKER_FIT = 'POOR_WORKER_FIT',
  POOR_WORKER_RELIABILITY = 'POOR_WORKER_RELIABILITY',
  HIGH_WORKER_TURNOVER = 'HIGH_WORKER_TURNOVER',
  GMP_NON_COMPLIANCE = 'GMP_NON_COMPLIANCE',
  PAY_ACCURACY = 'PAY_ACCURACY',
  INSUFFICIENT_WORKER_SUPPORT = 'INSUFFICIENT_WORKER_SUPPORT',
  TIME_SPENT_ON_WORKERS = 'TIME_SPENT_ON_WORKERS',
  MANUAL_VENDOR_COORDINATION = 'MANUAL_VENDOR_COORDINATION',
  REACTIVE_ISSUE_MANAGEMENT = 'REACTIVE_ISSUE_MANAGEMENT',
}

export enum TierRequirement {
  NONE = 'NONE',
  TIER_1 = 'TIER_1',
  TIER_2 = 'TIER_2',
  TIER_3 = 'TIER_3',
  UNPROVEN = 'UNPROVEN',
  TIER_4 = 'TIER_4',
  TIER_5 = 'TIER_5',
}

export enum CommonHoliday {
  NEW_YEARS_DAY = 'NEW_YEARS_DAY',
  MLK_DAY = 'MLK_DAY',
  PRESIDENTS_DAY = 'PRESIDENTS_DAY',
  MEMORIAL_DAY = 'MEMORIAL_DAY',
  INDEPENDENCE_DAY = 'INDEPENDENCE_DAY',
  LABOR_DAY = 'LABOR_DAY',
  COLUMBUS_DAY = 'COLUMBUS_DAY',
  VETERANS_DAY = 'VETERANS_DAY',
  THANKSGIVING = 'THANKSGIVING',
  DAY_AFTER_THANKSGIVING = 'DAY_AFTER_THANKSGIVING',
  CHRISTMAS_EVE = 'CHRISTMAS_EVE',
  CHRISTMAS_DAY = 'CHRISTMAS_DAY',
  NEW_YEARS_EVE = 'NEW_YEARS_EVE',
}

export enum HolidayType {
  COMMON = 'COMMON',
  CUSTOM = 'CUSTOM',
}

export interface CommonHolidayValue {
  type: HolidayType.COMMON
  value: CommonHoliday
}

export interface CustomHolidayValue {
  type: HolidayType.CUSTOM
  value: string
}

export type HolidayValue = CommonHolidayValue | CustomHolidayValue

// ── Fact Value Interfaces ───────────────────────────────────────────

export interface SingleSelectFactValue {
  version: 'SINGLE_SELECT_V1'
  value: string
  justification?: string
}

export interface SingleSelectWithNotesV1FactValue {
  version: 'SINGLE_SELECT_WITH_NOTES_V1'
  value: string
  notes?: string
  justification?: string
}

export interface TextV3FactValue {
  version: 'TEXT_V3'
  headline?: string
  details: string
  justification?: string
}

export interface EmailFactValue {
  version: 'EMAIL_V1'
  value: string
  justification?: string
}

export interface NumberV1FactValue {
  version: 'NUMBER_V1'
  value: number
  justification?: string
}

export interface NumberWithNotesV1FactValue {
  version: 'NUMBER_WITH_NOTES_V1'
  value: number
  notes?: string
  justification?: string
}

export interface ConversionFeeTier {
  minHours: number
  maxHours?: number | null
  feeAmountDollars?: number
  feePercentage?: number
}

export interface ConversionFeeV1FactValue {
  version: 'CONVERSION_FEE_V1'
  tiers: ConversionFeeTier[]
  notes?: string
  justification?: string
}

export interface PainPointFactValue {
  version: 'PAIN_POINT_V1'
  bucket: PainPointBucket
  issue: PainPointIssue
  justification?: string
}

export enum ContactResponsibilityKind {
  COMMON = 'COMMON',
  CUSTOM = 'CUSTOM',
}

export enum ContactResponsibilityTag {
  POC_COMMS = 'POC_COMMS',
  POC_ONBOARDING = 'POC_ONBOARDING',
  POC_TIMEKEEPING = 'POC_TIMEKEEPING',
  POC_TIMESHEETS = 'POC_TIMESHEETS',
  POC_BILLING = 'POC_BILLING',
  POC_INVOICE_APPROVAL = 'POC_INVOICE_APPROVAL',
  POC_SAFETY = 'POC_SAFETY',
  POC_RESUMES = 'POC_RESUMES',
  POC_INTERVIEWS = 'POC_INTERVIEWS',
  POC_CALL_OUTS = 'POC_CALL_OUTS',
}

export interface CommonContactResponsibility {
  type: ContactResponsibilityKind.COMMON
  value: ContactResponsibilityTag
  note?: string
}

export interface CustomContactResponsibility {
  type: ContactResponsibilityKind.CUSTOM
  value: string
  note?: string
}

export type ContactResponsibility =
  | CommonContactResponsibility
  | CustomContactResponsibility

export interface ContactFactValue {
  version: 'CONTACT_V1'
  name: string
  title?: string
  location?: string
  email?: string
  phone?: string
  preferredContactMethod?: string
  notes?: string
  responsibilities: ContactResponsibility[]
  justification?: string
}

export interface AddressV1Value {
  street1: string
  street2?: string
  city: string
  state: string
  postalCode: string
}

export interface AddressV1FactValue {
  version: 'ADDRESS_V1'
  address: AddressV1Value
  justification?: string
}

export interface OvertimePayV1FactValue {
  version: 'OVERTIME_PAY_V1'
  trigger?: OvertimePayTrigger
  multiplier?: OvertimePayMultiplier
  notes?: string
  justification?: string
}

export interface TimesheetScheduleV1FactValue {
  version: 'TIMESHEET_SCHEDULE_V1'
  day?: TimesheetScheduleDay
  time?: TimesheetScheduleTime
  notes?: string
  justification?: string
}

export interface YesNoWithNotesFactValue {
  version: 'YES_NO_WITH_NOTES_V1'
  isAllowed: boolean
  notes?: string
  justification?: string
}

export interface TierRequirementV1FactValue {
  version: 'TIER_REQUIREMENT_V1'
  tier: TierRequirement
  notes?: string
  justification?: string
}

export interface DrugTestV1FactValue {
  version: 'DRUG_TEST_V1'
  panelType: DrugPanelType
  provider?: string
  requiredBeforeStart?: boolean
  justification?: string
}

/**
 * Canonical drug substances tested in screening panels. Source of truth for
 * the substance taxonomy across worker-app, ops, and backend.
 */
export enum DrugSubstance {
  AMPHETAMINES = 'AMPHETAMINES',
  COCAINE = 'COCAINE',
  MARIJUANA = 'MARIJUANA',
  OPIATES = 'OPIATES',
  PCP = 'PCP',
  BARBITURATES = 'BARBITURATES',
  BENZODIAZEPINES = 'BENZODIAZEPINES',
  METHADONE = 'METHADONE',
  PROPOXYPHENE = 'PROPOXYPHENE',
  METHAQUALONE = 'METHAQUALONE',
  MDMA = 'MDMA',
  OXYCODONE = 'OXYCODONE',
  BUPRENORPHINE = 'BUPRENORPHINE',
}

/**
 * Standardized colloquial panel labels used in employer documents and Traba's
 * scheduling configuration. FIVE/SIX/TEN/THIRTEEN have canonical compositions
 * defined in PANEL_SUBSTANCE_MAPPING. OTHER signals a non-standard composition
 * that must be captured substance-by-substance.
 */
export enum DrugPanel {
  FIVE_PANEL = 'FIVE_PANEL',
  SIX_PANEL = 'SIX_PANEL',
  TEN_PANEL = 'TEN_PANEL',
  THIRTEEN_PANEL = 'THIRTEEN_PANEL',
  OTHER = 'OTHER',
}

/**
 * Traba's canonical substance composition for each standardized panel label.
 * 5-panel matches DOT; 10-panel matches the SAMHSA convention; 13-panel matches
 * Quest's SAP-13. 6-panel is not industry-standardized — this mapping uses
 * 5-panel + Oxycodone, matching both Quest Express 6 and LabCorp Express 6
 * (the SKUs Traba schedules). When an employer document specifies a different
 * 6-panel composition, the captured substances list takes precedence.
 *
 * OTHER is intentionally excluded — non-standard panels must capture composition
 * explicitly rather than rely on a default.
 */
export const PANEL_SUBSTANCE_MAPPING: Record<
  Exclude<DrugPanel, DrugPanel.OTHER>,
  DrugSubstance[]
> = {
  [DrugPanel.FIVE_PANEL]: [
    DrugSubstance.AMPHETAMINES,
    DrugSubstance.COCAINE,
    DrugSubstance.MARIJUANA,
    DrugSubstance.OPIATES,
    DrugSubstance.PCP,
  ],
  [DrugPanel.SIX_PANEL]: [
    DrugSubstance.AMPHETAMINES,
    DrugSubstance.COCAINE,
    DrugSubstance.MARIJUANA,
    DrugSubstance.OPIATES,
    DrugSubstance.PCP,
    DrugSubstance.OXYCODONE,
  ],
  [DrugPanel.TEN_PANEL]: [
    DrugSubstance.AMPHETAMINES,
    DrugSubstance.COCAINE,
    DrugSubstance.MARIJUANA,
    DrugSubstance.OPIATES,
    DrugSubstance.PCP,
    DrugSubstance.BARBITURATES,
    DrugSubstance.BENZODIAZEPINES,
    DrugSubstance.METHADONE,
    DrugSubstance.PROPOXYPHENE,
    DrugSubstance.METHAQUALONE,
  ],
  [DrugPanel.THIRTEEN_PANEL]: [
    DrugSubstance.AMPHETAMINES,
    DrugSubstance.COCAINE,
    DrugSubstance.MARIJUANA,
    DrugSubstance.OPIATES,
    DrugSubstance.PCP,
    DrugSubstance.BARBITURATES,
    DrugSubstance.BENZODIAZEPINES,
    DrugSubstance.METHADONE,
    DrugSubstance.PROPOXYPHENE,
    DrugSubstance.METHAQUALONE,
    DrugSubstance.OXYCODONE,
    DrugSubstance.MDMA,
    DrugSubstance.BUPRENORPHINE,
  ],
}

export interface PreferredLab {
  vendor?: DrugScreeningVendor
  note?: string
}

export interface DrugTestV2FactValue {
  version: 'DRUG_TEST_V2'
  panelLabel: DrugPanel
  substances: DrugSubstance[]
  otherSubstances: string[]
  requiredBeforeStart?: boolean
  collectionMethod?: DrugCollectionMethod
  trigger?: DrugTestTrigger
  preferredLab?: PreferredLab
  alternativeTestNote?: string
  justification?: string
}

export interface CallOutLineV1FactValue {
  version: 'CALL_OUT_LINE_V1'
  hasCallOutLine: boolean
  phoneNumber?: string
  justification?: string
}

export interface ScheduledBreakEntry {
  lengthMinutes: number
  count: number
}

export interface ScheduledBreaksV1FactValue {
  version: 'SCHEDULED_BREAKS_V1'
  breakType: BreakTypeOption
  breaks: ScheduledBreakEntry[]
  justification?: string
}

export interface HolidayV1FactValue {
  version: 'HOLIDAY_V1'
  holiday: HolidayValue
  note?: string
  justification?: string
}

export interface SuccessMetricV1FactValue {
  version: 'SUCCESS_METRIC_V1'
  metricType: string
  benchmarkPercentage?: number
  notes?: string
  justification?: string
}

export type FactValue =
  | SingleSelectFactValue
  | SingleSelectWithNotesV1FactValue
  | TextV3FactValue
  | EmailFactValue
  | NumberV1FactValue
  | NumberWithNotesV1FactValue
  | ConversionFeeV1FactValue
  | PainPointFactValue
  | ContactFactValue
  | OvertimePayV1FactValue
  | TimesheetScheduleV1FactValue
  | YesNoWithNotesFactValue
  | TierRequirementV1FactValue
  | DrugTestV1FactValue
  | DrugTestV2FactValue
  | CallOutLineV1FactValue
  | ScheduledBreaksV1FactValue
  | HolidayV1FactValue
  | SuccessMetricV1FactValue
  | OnsiteStrategyV1FactValue
  | AddressV1FactValue

// ── Zod Schemas ─────────────────────────────────────────────────────

const JustificationSchema = z.string().optional()

export const SingleSelectFactValueSchema = z.object({
  version: z.literal(CompanyFactSchemaVersion.SINGLE_SELECT_V1),
  value: z.string().min(1),
  justification: JustificationSchema,
})

export const SingleSelectWithNotesV1FactValueSchema = z.object({
  version: z.literal(CompanyFactSchemaVersion.SINGLE_SELECT_WITH_NOTES_V1),
  value: z.string().min(1),
  notes: z.string().optional(),
  justification: JustificationSchema,
})

export const TextV3FactValueSchema = z.object({
  version: z.literal(CompanyFactSchemaVersion.TEXT_V3),
  headline: z.string().optional(),
  details: z.string(),
  justification: JustificationSchema,
})

export const EmailFactValueSchema = z.object({
  version: z.literal(CompanyFactSchemaVersion.EMAIL_V1),
  value: z.string().email(),
  justification: JustificationSchema,
})

export const NumberV1FactValueSchema = z.object({
  version: z.literal(CompanyFactSchemaVersion.NUMBER_V1),
  value: z.number(),
  justification: JustificationSchema,
})

export const NumberWithNotesV1FactValueSchema = z.object({
  version: z.literal(CompanyFactSchemaVersion.NUMBER_WITH_NOTES_V1),
  value: z.number(),
  notes: z.string().optional(),
  justification: JustificationSchema,
})

export const ConversionFeeTierSchema = z
  .object({
    minHours: z.number().int().min(0),
    maxHours: z.number().int().min(0).nullish(),
    feeAmountDollars: z.number().min(0).optional(),
    feePercentage: z.number().min(0).optional(),
  })
  .refine(
    (tier) =>
      tier.feeAmountDollars !== undefined || tier.feePercentage !== undefined,
    {
      message: 'Either feeAmountDollars or feePercentage must be provided',
    },
  )

export const ConversionFeeV1FactValueSchema = z.object({
  version: z.literal(CompanyFactSchemaVersion.CONVERSION_FEE_V1),
  tiers: z.array(ConversionFeeTierSchema),
  notes: z.string().optional(),
  justification: JustificationSchema,
})

export const PainPointFactValueSchema = z.object({
  version: z.literal(CompanyFactSchemaVersion.PAIN_POINT_V1),
  bucket: z.nativeEnum(PainPointBucket),
  issue: z.nativeEnum(PainPointIssue),
  justification: JustificationSchema,
})

export const CommonContactResponsibilitySchema = z.object({
  type: z.literal(ContactResponsibilityKind.COMMON),
  value: z.nativeEnum(ContactResponsibilityTag),
  note: z.string().optional(),
})

export const CustomContactResponsibilitySchema = z.object({
  type: z.literal(ContactResponsibilityKind.CUSTOM),
  value: z.string().trim().min(1),
  note: z.string().optional(),
})

export const ContactResponsibilitySchema = z.discriminatedUnion('type', [
  CommonContactResponsibilitySchema,
  CustomContactResponsibilitySchema,
])

export const ContactFactValueSchema = z.object({
  version: z.literal(CompanyFactSchemaVersion.CONTACT_V1),
  name: z.string().min(1),
  title: z.string().optional(),
  location: z.string().optional(),
  email: z.string().email().optional(),
  phone: z.string().optional(),
  preferredContactMethod: z.string().optional(),
  notes: z.string().optional(),
  responsibilities: z.array(ContactResponsibilitySchema).default([]),
  justification: JustificationSchema,
})

export const OvertimePayV1FactValueSchema = z.object({
  version: z.literal(CompanyFactSchemaVersion.OVERTIME_PAY_V1),
  trigger: z.nativeEnum(OvertimePayTrigger).optional(),
  multiplier: z.nativeEnum(OvertimePayMultiplier).optional(),
  notes: z.string().optional(),
  justification: JustificationSchema,
})

export const TimesheetScheduleV1FactValueSchema = z.object({
  version: z.literal(CompanyFactSchemaVersion.TIMESHEET_SCHEDULE_V1),
  day: z.nativeEnum(TimesheetScheduleDay).optional(),
  time: z.nativeEnum(TimesheetScheduleTime).optional(),
  notes: z.string().optional(),
  justification: JustificationSchema,
})

export const YesNoWithNotesFactValueSchema = z.object({
  version: z.literal(CompanyFactSchemaVersion.YES_NO_WITH_NOTES_V1),
  isAllowed: z.boolean(),
  notes: z.string().optional(),
  justification: JustificationSchema,
})

export const TierRequirementV1FactValueSchema = z.object({
  version: z.literal(CompanyFactSchemaVersion.TIER_REQUIREMENT_V1),
  tier: z.nativeEnum(TierRequirement),
  notes: z.string().optional(),
  justification: JustificationSchema,
})

export const DrugTestV1FactValueSchema = z.object({
  version: z.literal(CompanyFactSchemaVersion.DRUG_TEST_V1),
  panelType: z.nativeEnum(DrugPanelType),
  provider: z.string().optional(),
  requiredBeforeStart: z.boolean().optional(),
  justification: JustificationSchema,
})

export const PreferredLabSchema = z.object({
  vendor: z.nativeEnum(DrugScreeningVendor).optional(),
  note: z.string().min(1).optional(),
})

export const DrugTestV2FactValueSchema = z.object({
  version: z.literal(CompanyFactSchemaVersion.DRUG_TEST_V2),
  panelLabel: z.nativeEnum(DrugPanel),
  substances: z.array(z.nativeEnum(DrugSubstance)),
  otherSubstances: z.array(z.string()),
  requiredBeforeStart: z.boolean().optional(),
  collectionMethod: z.nativeEnum(DrugCollectionMethod).optional(),
  trigger: z.nativeEnum(DrugTestTrigger).optional(),
  preferredLab: PreferredLabSchema.optional(),
  alternativeTestNote: z.string().trim().min(1).optional(),
  justification: JustificationSchema,
})

export const CallOutLineV1FactValueSchema = z.object({
  version: z.literal(CompanyFactSchemaVersion.CALL_OUT_LINE_V1),
  hasCallOutLine: z.boolean(),
  phoneNumber: z.string().optional(),
  justification: JustificationSchema,
})

export const ScheduledBreakEntrySchema = z.object({
  lengthMinutes: z.number().int().min(1),
  count: z.number().int().min(1),
})

export const ScheduledBreaksV1FactValueSchema = z.object({
  version: z.literal(CompanyFactSchemaVersion.SCHEDULED_BREAKS_V1),
  breakType: z.nativeEnum(BreakTypeOption),
  breaks: z.array(ScheduledBreakEntrySchema),
  justification: JustificationSchema,
})

export const CommonHolidaySchema = z.object({
  type: z.literal(HolidayType.COMMON),
  value: z.nativeEnum(CommonHoliday),
})

export const CustomHolidaySchema = z.object({
  type: z.literal(HolidayType.CUSTOM),
  value: z.string().min(1),
})

export const HolidaySchema = z.discriminatedUnion('type', [
  CommonHolidaySchema,
  CustomHolidaySchema,
])

export const HolidayV1FactValueSchema = z.object({
  version: z.literal(CompanyFactSchemaVersion.HOLIDAY_V1),
  holiday: HolidaySchema,
  note: z.string().optional(),
  justification: JustificationSchema,
})

export const SuccessMetricV1FactValueSchema = z.object({
  version: z.literal(CompanyFactSchemaVersion.SUCCESS_METRIC_V1),
  metricType: z
    .string()
    .trim()
    .min(1)
    .describe('Success metric identity; known enum value or custom label'),
  benchmarkPercentage: z.number().optional(),
  notes: z.string().optional(),
  justification: JustificationSchema,
})

export const AddressV1ValueSchema = z.object({
  street1: z.string().min(1),
  street2: z.string().optional(),
  city: z.string().min(1),
  state: z.string().min(1),
  postalCode: z.string().min(1),
})

export const AddressV1FactValueSchema = z.object({
  version: z.literal(CompanyFactSchemaVersion.ADDRESS_V1),
  address: AddressV1ValueSchema,
  justification: JustificationSchema,
})

export const FactValueSchema = z.discriminatedUnion('version', [
  SingleSelectFactValueSchema,
  SingleSelectWithNotesV1FactValueSchema,
  TextV3FactValueSchema,
  EmailFactValueSchema,
  NumberV1FactValueSchema,
  NumberWithNotesV1FactValueSchema,
  ConversionFeeV1FactValueSchema,
  PainPointFactValueSchema,
  ContactFactValueSchema,
  OvertimePayV1FactValueSchema,
  TimesheetScheduleV1FactValueSchema,
  YesNoWithNotesFactValueSchema,
  TierRequirementV1FactValueSchema,
  DrugTestV1FactValueSchema,
  DrugTestV2FactValueSchema,
  CallOutLineV1FactValueSchema,
  ScheduledBreaksV1FactValueSchema,
  HolidayV1FactValueSchema,
  SuccessMetricV1FactValueSchema,
  OnsiteStrategyV1FactValueSchema,
  AddressV1FactValueSchema,
])
